/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   VIEWS / ADMIN / STATS-COLLAPSE — Top-5-Rangliste mit „alle anzeigen" (S217 Cut 3)
   =============================================================================
   Grundmuster für ALLE Statistik-Ranglisten (Jonas-Motto): Ranglisten immer über
   das GESAMTE Angebot rechnen (RPCs cappen nicht — live verifiziert), Anzeige zeigt
   die Top 5, der Rest ist per „alle anzeigen (N)" aufklappbar.

   Non-destruktiv (Memory feedback_gg_non_destructive_render): die vollständige
   Liste steht immer im DOM; das Ein-/Ausklappen ist reines Sichtbar-Schalten (CSS
   nth-child) + Modul-State-Set, damit ein Re-Render (Zeitraumwechsel/Realtime) den
   Aufklapp-Zustand nicht zurücksetzt. Toggle-Handler CSP-konform (func(this), kein
   window.-Prefix, Argument über data-Attribut → kein String-Quoting).
============================================================================ */

import { htmlEscape } from '../../utils/helpers.js';

const TOP_DEFAULT = 5;

// Welche Ranglisten (per stabilem key) sind aufgeklappt — Modul-State, überlebt
// Re-Render (Zeitraumwechsel/Realtime).
const _openRanks = new Set();

/** Ist die Rangliste `key` aktuell aufgeklappt? */
export function isRankOpen(key) { return _openRanks.has(key); }

/** Gemeinsamer Collapse-Wrapper: `bodyHtml` MUSS ein Element mit Klasse
 *  `stats-rank-body` sein (dessen direkte Kinder = die Zeilen). CSS blendet ab dem
 *  (top+1)-ten Kind aus (display:none !important — schlägt Inline-`display:flex`),
 *  solange nicht `.is-open`. Toggle-Button nur, wenn mehr als `top` Zeilen. */
function _wrap(key, bodyHtml, total, top) {
  const open = _openRanks.has(key);
  const hasMore = (Number(total) || 0) > top;
  const btn = hasMore
    ? `<button type="button" class="stats-rank-toggle" onclick="statsToggleRank(this)">${
        open ? 'weniger anzeigen' : `alle anzeigen (${Number(total) || 0})`}</button>`
    : '';
  return `<div class="stats-ranklist${open ? ' is-open' : ''}" data-rankkey="${htmlEscape(key)}" data-total="${Number(total) || 0}">
    ${bodyHtml}
    ${btn}
  </div>`;
}

/**
 * Kollabierbare Rangliste aus <li>-Zeilen: rendert eine <ol class="stats-rank
 * stats-rank-body"> mit ALLEN Zeilen; eingeklappt sind nur die ersten `top`
 * sichtbar, per „alle anzeigen (N)" aufklappbar.
 *   key    — stabiler, tab-übergreifend eindeutiger Schlüssel (Persistenz)
 *   liHtml — bereits gebauter <li>…</li>-String ALLER Einträge
 *   total  — Gesamtanzahl (Button-Label)
 */
export function collapsibleRankHtml(key, liHtml, total, top = TOP_DEFAULT) {
  return _wrap(key, `<ol class="stats-rank stats-rank-body" style="margin:0;padding:0;list-style:none;">${liHtml}</ol>`, total, top);
}

/**
 * Wie collapsibleRankHtml, aber für beliebige Block-Zeilen (z. B. Balken-<div>s
 * statt <li>). `rowsHtml` = die fertigen Zeilen-Elemente; sie werden in einen
 * `.stats-rank-body`-Container gehängt (direkte Kinder = Zeilen).
 */
export function collapsibleRowsHtml(key, rowsHtml, total, top = TOP_DEFAULT) {
  return _wrap(key, `<div class="stats-rank-body">${rowsHtml}</div>`, total, top);
}

/** Toggle-Handler (window-registriert). Schaltet Sichtbarkeit + Modul-State +
 *  Button-Label um — ohne Daten neu zu laden (die Liste ist schon vollständig da). */
export function statsToggleRank(el) {
  const wrap = el && el.closest ? el.closest('.stats-ranklist') : null;
  if (!wrap) return;
  const key = wrap.dataset.rankkey;
  const open = wrap.classList.toggle('is-open');
  if (key) { if (open) _openRanks.add(key); else _openRanks.delete(key); }
  el.textContent = open ? 'weniger anzeigen' : `alle anzeigen (${wrap.dataset.total || 0})`;
}

if (typeof window !== 'undefined') {
  window.statsToggleRank = statsToggleRank;
}
